import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

export interface CampaignInsightsSummary {
  emails_sent: string | number | null
  total_leads_contacted: string | number | null
  opened: string | number | null
  opened_percentage: string | number | null
  unique_opens_per_contact: string | number | null
  unique_opens_per_contact_percentage: string | number | null
  unique_replies_per_contact: string | number | null
  unique_replies_per_contact_percentage: string | number | null
  bounced: string | number | null
  bounced_percentage: string | number | null
  unsubscribed: string | number | null
  unsubscribed_percentage: string | number | null
  interested: string | number | null
  interested_percentage: string | number | null
  sequence_step_stats?: Array<{
    sequence_step_id: number
    email_subject: string
    sent: number
    leads_contacted: number
    unique_opens: number
    unique_replies: number
    unsubscribed: number
    bounced: number
    interested: number
  }>
}

export interface CampaignInsightsSeries {
  label: string
  color: string
  dates: Array<[string, number]>
}

export interface CampaignInsightsResponse {
  start_date: string
  end_date: string
  summary: CampaignInsightsSummary
  series: CampaignInsightsSeries[]
}

export interface CampaignLaunchReadiness {
  campaign_id: number
  bison_campaign_id: number | null
  status: string
  sequence: {
    is_complete: boolean
    step_count: number
    description: string
  }
  leads: {
    is_complete: boolean
    count: number
    description: string
  }
  sender_emails: {
    is_complete: boolean
    count: number
    disconnected_emails: string[]
    description: string
  }
  schedule: {
    is_complete: boolean
    description: string
  }
  all_complete: boolean
}

export const campaignInsightsKeys = {
  all: ['campaign-insights'] as const,
  byCampaign: (campaignId: number) =>
    [...campaignInsightsKeys.all, 'campaign', campaignId] as const,
}

export const campaignLaunchReadinessKeys = {
  all: ['campaign-launch-readiness'] as const,
  byCampaign: (campaignId: number) =>
    [...campaignLaunchReadinessKeys.all, 'campaign', campaignId] as const,
}

function getDefaultCampaignLaunchReadiness(
  campaignId: number
): CampaignLaunchReadiness {
  return {
    campaign_id: campaignId,
    bison_campaign_id: null,
    status: 'draft',
    sequence: {
      is_complete: false,
      step_count: 0,
      description: 'No steps configured',
    },
    leads: {
      is_complete: false,
      count: 0,
      description: 'No leads synced',
    },
    sender_emails: {
      is_complete: false,
      count: 0,
      disconnected_emails: [],
      description: 'No sender emails attached',
    },
    schedule: {
      is_complete: false,
      description: 'No schedule configured',
    },
    all_complete: false,
  }
}

const getCampaignInsights = async (
  campaignId: number
): Promise<CampaignInsightsResponse | null> => {
  try {
    return await baseAPICall<CampaignInsightsResponse | null>(
      () => clientInstance.get(`/emailbison/campaigns/${campaignId}/insights/`),
      {
        disableNotifyGenericError: true,
      }
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

const getCampaignLaunchReadiness = async (
  campaignId: number
): Promise<CampaignLaunchReadiness> => {
  try {
    return await baseAPICall<CampaignLaunchReadiness>(
      () =>
        clientInstance.get(
          `/emailbison/campaigns/${campaignId}/launch-readiness/`
        ),
      {
        disableNotifyGenericError: true,
      }
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return getDefaultCampaignLaunchReadiness(campaignId)
    }
    throw error
  }
}

export const useGetCampaignInsights = (campaignId: number, enabled = true) => {
  return useQuery({
    queryKey: campaignInsightsKeys.byCampaign(campaignId),
    queryFn: () => getCampaignInsights(campaignId),
    enabled: enabled && campaignId > 0,
    retry: false,
  })
}

export const useGetCampaignLaunchReadiness = (
  campaignId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: campaignLaunchReadinessKeys.byCampaign(campaignId),
    queryFn: () => getCampaignLaunchReadiness(campaignId),
    enabled: enabled && campaignId > 0,
    retry: false,
  })
}
